import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Handshake, Plus, Edit, Trash, RefreshCw, Save, FileSpreadsheet, Upload, Eye, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { canonicalPayrollCode, getPayrollDateRange } from '../payroll/core/domain';
import { compileTemplateDraft, normalizeAgreementValues, planAgreementSuccession, validateDateRange } from '../payroll/core/configRules';
import {
    assembleTemplates, buildTeamsByEmployee, isEmployeeInPeriod, resolveAgreementsAt, resolvePeriodEmployees,
} from '../payroll/core/periodInputs';
import { addAgreements, deleteAgreement, loadTemplateData, saveAgreement } from '../payroll/data/configActions';
import { PAYROLL_PATHS, canManagePayroll, canViewPayrollPage } from '../payroll/data/permissions';
import { parseSheetNumber } from '../payroll/data/sheetFormat';
import { parseExcelDate } from '../payroll/components/excelIO';
import {
    AccessDenied, Badge, ConfirmDialog, EmptyState, Field, FilterField, IconAction, ListToolbar, MessageList, Modal, PageShell, Pagination,
    NumberInput, SearchSelect, SectionTitle, SortableTh, Spinner, StatCards, buttonPrimary, buttonSecondary, employeeOptions, formatDay, formatMoney,
    fromInputDate, inputClass, matchesSearch, tableClass, tbodyClass, tdClass, thClass, theadClass, toInputDate, usePagination, useSortedRows,
} from '../payroll/components/ui';

const startOfToday = () => { const today = new Date(); return new Date(today.getFullYear(), today.getMonth(), today.getDate()); };
const firstDayOfMonth = () => { const today = new Date(); return new Date(today.getFullYear(), today.getMonth(), 1); };

const STATUS_INFO = {
    ACTIVE: { label: 'Đang hiệu lực', tone: 'green' },
    FUTURE: { label: 'Sắp hiệu lực', tone: 'blue' },
    EXPIRED: { label: 'Hết hiệu lực', tone: 'gray' },
};

function agreementStatus(agreement, today = startOfToday()) {
    if (agreement.HIEU_LUC_TU && agreement.HIEU_LUC_TU > today) return 'FUTURE';
    if (agreement.HIEU_LUC_DEN && agreement.HIEU_LUC_DEN < today) return 'EXPIRED';
    return 'ACTIVE';
}

// Mức chọn nhanh cho ô tỷ lệ (%), ví dụ tỷ lệ giữ lại PCTN: không giữ, giữ một nửa, giữ hết.
const PERCENT_PRESETS = [0, 50, 100];

/** Biến thỏa thuận của mẫu (theo thứ tự cột), các biến bắt buộc và các biến tỷ lệ (%). */
function agreementFieldsOf(template, variables) {
    if (!template) return { codes: [], required: [], percent: [] };
    const compiled = compileTemplateDraft(template.CHI_TIETS.map((column) => ({ ...column, BIEN_ID: canonicalPayrollCode(column.BIEN_ID) })), variables);
    const columns = compiled.columns.filter((column) => column.source === 'THOA_THUAN');
    const percentCodes = new Set(variables.filter((variable) => variable.LOAI_DU_LIEU === 'TY_LE').map((variable) => canonicalPayrollCode(variable.MA_BIEN)));
    return {
        codes: columns.map((column) => column.code),
        required: columns.filter((column) => column.required).map((column) => column.code),
        percent: columns.filter((column) => percentCodes.has(column.code)).map((column) => column.code),
    };
}

function AgreementForm({ initial, isNew, data, templates, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [succession, setSuccession] = useState(null);
    const template = templates.find((item) => item.MA_MAU === form.MAU_ID);
    const fields = useMemo(() => agreementFieldsOf(template, data.variables), [template, data.variables]);
    const variableByCode = useMemo(() => new Map(data.variables.map((variable) => [canonicalPayrollCode(variable.MA_BIEN), variable])), [data.variables]);
    const parsed = Object.fromEntries(Object.entries(form.values).map(([code, raw]) => [code, raw === '' ? null : parseSheetNumber(raw, null)]));
    const { values, missing, outOfRange } = normalizeAgreementValues(parsed, fields.codes, fields.required, fields.percent);
    const invalid = fields.codes.filter((code) => form.values[code] && Number.isNaN(parsed[code]));
    const from = fromInputDate(form.from);
    const to = fromInputDate(form.to);
    const errors = [
        ...(!form.NHAN_SU_ID ? ['Chọn nhân sự.'] : []),
        ...(!form.MAU_ID ? ['Chọn mẫu bảng lương.'] : []),
        ...[validateDateRange(from, to)].filter(Boolean),
        ...missing.map((code) => `Thiếu ${code} (${variableByCode.get(code)?.TEN_BIEN || code}).`),
        ...invalid.map((code) => `${code} phải là số.`),
        ...outOfRange.map((code) => `${code} (${variableByCode.get(code)?.TEN_BIEN || code}) phải từ 0 đến 100%.`),
    ];
    const candidate = { ID: form.ID, NHAN_SU_ID: form.NHAN_SU_ID, MAU_ID: form.MAU_ID, HIEU_LUC_TU: from, HIEU_LUC_DEN: to, GIA_TRI: values, GHI_CHU: form.GHI_CHU };

    const save = async (closeAgreements = []) => {
        try {
            setSaving(true);
            await saveAgreement(candidate, { isNew, closeAgreements });
            toast.success(isNew ? 'Đã thêm thỏa thuận lương' : 'Đã cập nhật thỏa thuận lương');
            onSaved();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        const plan = planAgreementSuccession(data.agreements, candidate);
        if (plan.blocking.length) {
            toast.error(`Nhân sự đã có thỏa thuận chồng thời gian (${plan.blocking.map((item) => `${item.MAU_ID} từ ${formatDay(item.HIEU_LUC_TU)}`).join(', ')}). Sửa ngày của thỏa thuận đó trước.`);
            return;
        }
        if (plan.closable.length) setSuccession(plan.closable);
        else save();
    };

    return (
        <>
            <Modal title={isNew ? 'Thêm thỏa thuận lương' : 'Cập nhật thỏa thuận lương'} subtitle="Giá trị được áp dụng theo thời gian hiệu lực và dùng khi tính kỳ lương" size="max-w-4xl" onClose={onClose} disabled={saving}
                footer={(
                    <>
                        <button className={buttonSecondary} onClick={onClose} disabled={saving}>Hủy</button>
                        <button className={buttonPrimary} onClick={handleSave} disabled={saving || errors.length > 0}><Save className="w-4 h-4" />{saving ? 'Đang lưu...' : 'Lưu'}</button>
                    </>
                )}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <SectionTitle>Thông tin áp dụng</SectionTitle>
                        <Field label="Nhân sự" required>
                            <SearchSelect options={employeeOptions(data.employees)} value={form.NHAN_SU_ID} disabled={!isNew}
                                onChange={(value) => setForm((prev) => ({ ...prev, NHAN_SU_ID: value }))} placeholder="Tìm và chọn nhân sự..." />
                        </Field>
                        <Field label="Mẫu lương" required>
                            <select className={inputClass} value={form.MAU_ID} onChange={(e) => setForm((prev) => ({ ...prev, MAU_ID: e.target.value }))}>
                                <option value="">Chọn mẫu lương</option>
                                {templates.filter((item) => item.HOAT_DONG || item.MA_MAU === form.MAU_ID).map((item) => <option key={item.MA_MAU} value={item.MA_MAU}>{item.TEN_MAU} ({item.MA_MAU})</option>)}
                            </select>
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Hiệu lực từ" required>
                                <input type="date" className={inputClass} value={form.from} onChange={(e) => setForm((prev) => ({ ...prev, from: e.target.value }))} />
                            </Field>
                            <Field label="Hiệu lực đến" hint="Bỏ trống = không thời hạn">
                                <input type="date" className={inputClass} value={form.to} onChange={(e) => setForm((prev) => ({ ...prev, to: e.target.value }))} />
                            </Field>
                        </div>
                        <Field label="Ghi chú">
                            <textarea rows={3} className={inputClass} value={form.GHI_CHU} placeholder="Thông tin bổ sung cho thỏa thuận" onChange={(e) => setForm((prev) => ({ ...prev, GHI_CHU: e.target.value }))} />
                        </Field>
                    </div>
                    <div className="space-y-4">
                        <SectionTitle>Giá trị theo mẫu lương</SectionTitle>
                        {!template && <p className="text-sm text-gray-500">Chọn mẫu lương để hiện các khoản cần thỏa thuận.</p>}
                        {template && fields.codes.length === 0 && <p className="text-sm text-gray-500">Mẫu này không có khoản nào cần thỏa thuận.</p>}
                        {fields.codes.map((code) => {
                            const variable = variableByCode.get(code);
                            const required = fields.required.includes(code);
                            const percent = fields.percent.includes(code);
                            return (
                                <Field key={code} label={`${variable?.TEN_BIEN || code} (${code})${variable?.DON_VI ? ` — ${variable.DON_VI}` : ''}`} required={required}
                                    hint={percent ? 'Bấm 0%, 50% hoặc 100%; mức khác thì gõ vào ô bên cạnh (0 đến 100).' : required ? null : 'Bỏ trống nếu người này không có khoản này (tính bằng 0).'}>
                                    {percent ? (
                                        <div className="flex items-center gap-2">
                                            <div className="flex border border-gray-300 rounded-lg overflow-hidden shadow-sm" role="group" aria-label={`Chọn nhanh ${code}`}>
                                                {PERCENT_PRESETS.map((preset) => {
                                                    const active = form.values[code] !== '' && form.values[code] != null && Number(parsed[code]) === preset;
                                                    return (
                                                        <button key={preset} type="button" aria-pressed={active}
                                                            onClick={() => setForm((prev) => ({ ...prev, values: { ...prev.values, [code]: String(preset) } }))}
                                                            className={`px-4 py-2 text-sm font-medium border-r border-gray-300 last:border-r-0 ${active ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                                                            {preset}%
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className="w-32">
                                                <NumberInput value={form.values[code] ?? ''} placeholder="Mức khác" aria-label={`${code} mức khác`}
                                                    onChange={(next) => setForm((prev) => ({ ...prev, values: { ...prev.values, [code]: next } }))} />
                                            </div>
                                        </div>
                                    ) : (
                                        <NumberInput value={form.values[code] ?? ''} placeholder={variable?.DON_VI === 'Đồng' ? 'Ví dụ 8.000.000' : 'Ví dụ 1 hoặc 1,5'}
                                            onChange={(next) => setForm((prev) => ({ ...prev, values: { ...prev.values, [code]: next } }))} />
                                    )}
                                </Field>
                            );
                        })}
                    </div>
                </div>
                {errors.length > 0 && <div className="mt-4"><MessageList items={errors} /></div>}
            </Modal>
            {succession && (
                <ConfirmDialog title="Kết thúc thỏa thuận cũ" danger={false} confirmLabel="Lưu và kết thúc thỏa thuận cũ" onClose={() => setSuccession(null)}
                    message={<>Thỏa thuận cũ ({succession.map((item) => `${item.MAU_ID} từ ${formatDay(item.HIEU_LUC_TU)}`).join(', ')}) sẽ kết thúc vào ngày <b>{formatDay(succession[0].HIEU_LUC_DEN)}</b>, thỏa thuận mới áp dụng từ <b>{formatDay(from)}</b>.</>}
                    onConfirm={async () => { await save(succession); setSuccession(null); }} />
            )}
        </>
    );
}

function AgreementDetail({ row, template, variableByCode, onClose }) {
    return (
        <Modal title={row.employee?.HO_TEN || row.NHAN_SU_ID} subtitle={`Số HĐ ${row.NHAN_SU_ID}${row.employee?.TO ? ` · Tổ ${row.employee.TO}` : ''}`} onClose={onClose}
            footer={<button className={buttonSecondary} onClick={onClose}>Đóng</button>}>
            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div><p className="text-gray-500">Mẫu lương</p><p className="font-medium text-gray-900">{template?.TEN_MAU || row.MAU_ID}</p></div>
                <div><p className="text-gray-500">Hiệu lực</p><p className="font-medium text-gray-900">{formatDay(row.HIEU_LUC_TU)} → {formatDay(row.HIEU_LUC_DEN) || 'không thời hạn'}</p></div>
                <div><p className="text-gray-500">Trạng thái</p><Badge tone={STATUS_INFO[row.status].tone}>{STATUS_INFO[row.status].label}</Badge></div>
                <div><p className="text-gray-500">Người tạo</p><p className="font-medium text-gray-900">{row.NGUOI_TAO || '—'}</p></div>
            </div>
            <SectionTitle>Giá trị thỏa thuận</SectionTitle>
            <table className={tableClass}>
                <tbody className={tbodyClass}>
                    {Object.entries(row.GIA_TRI ?? {}).map(([code, value]) => (
                        <tr key={code}>
                            <td className={tdClass}>{variableByCode.get(canonicalPayrollCode(code))?.TEN_BIEN || code} <span className="text-xs text-gray-400 font-mono">{code}</span></td>
                            <td className={`${tdClass} text-right font-semibold text-gray-900`}>{formatMoney(value)} {variableByCode.get(canonicalPayrollCode(code))?.DON_VI || ''}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {row.GHI_CHU && <p className="text-sm text-gray-600 mt-3">Ghi chú: {row.GHI_CHU}</p>}
        </Modal>
    );
}

function ImportModal({ data, templates, missingByTemplate, onClose, onImported }) {
    const [templateCode, setTemplateCode] = useState(templates.find((item) => item.HOAT_DONG)?.MA_MAU || '');
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const template = templates.find((item) => item.MA_MAU === templateCode);
    const fields = useMemo(() => agreementFieldsOf(template, data.variables), [template, data.variables]);
    const employeeBySoHd = useMemo(() => new Map(data.employees.map((employee) => [String(employee.SO_HD), employee])), [data.employees]);

    const handleDownload = () => {
        const people = missingByTemplate.get(templateCode) ?? [];
        const header = ['SỐ HĐ', 'HỌ TÊN', 'TỔ', 'MÃ MẪU', 'HIỆU LỰC TỪ (DD/MM/YYYY)', 'HIỆU LỰC ĐẾN (DD/MM/YYYY)', ...fields.codes, 'GHI CHÚ'];
        const from = formatDay(firstDayOfMonth());
        const rows = people.map((person) => [person.employeeCode, person.employeeName, person.team || '', templateCode, from, '', ...fields.codes.map(() => ''), '']);
        const sheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
        sheet['!cols'] = header.map((title) => ({ wch: Math.max(12, title.length + 2) }));
        const names = [['MÃ BIẾN', 'TÊN BIẾN', 'BẮT BUỘC'], ...fields.codes.map((code) => [code, data.variables.find((item) => canonicalPayrollCode(item.MA_BIEN) === code)?.TEN_BIEN || '', fields.required.includes(code) ? 'Có' : ''])];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, sheet, 'Thỏa thuận');
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(names), 'Giải thích cột');
        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `ThoaThuanLuong_${templateCode}.xlsx`);
    };

    const handleFile = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (loadEvent) => {
            try {
                const workbook = XLSX.read(loadEvent.target.result, { type: 'array', cellDates: true });
                const raw = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: '', raw: true });
                const accepted = [];
                const rows = raw.map((row, index) => {
                    const soHd = String(row['SỐ HĐ'] ?? '').trim();
                    const code = String(row['MÃ MẪU'] ?? '').trim().toUpperCase();
                    const rowTemplate = templates.find((item) => item.MA_MAU === code);
                    const rowFields = agreementFieldsOf(rowTemplate, data.variables);
                    const from = parseExcelDate(row['HIỆU LỰC TỪ (DD/MM/YYYY)'] ?? row['HIỆU LỰC TỪ']);
                    const to = parseExcelDate(row['HIỆU LỰC ĐẾN (DD/MM/YYYY)'] ?? row['HIỆU LỰC ĐẾN']);
                    const parsed = Object.fromEntries(rowFields.codes.map((field) => [field, row[field] === '' ? null : parseSheetNumber(row[field], null)]));
                    const { values, missing, outOfRange } = normalizeAgreementValues(parsed, rowFields.codes, rowFields.required, rowFields.percent);
                    const errors = [];
                    if (!employeeBySoHd.has(soHd)) errors.push(`Không có nhân sự Số HĐ ${soHd || '(trống)'}`);
                    if (!rowTemplate) errors.push(`Không có mẫu ${code || '(trống)'}`);
                    if (from === undefined || to === undefined) errors.push('Ngày phải dạng DD/MM/YYYY');
                    else { const rangeError = validateDateRange(from, to); if (rangeError) errors.push(rangeError); }
                    errors.push(...rowFields.codes.filter((field) => Number.isNaN(parsed[field])).map((field) => `${field} không phải số`));
                    errors.push(...missing.map((field) => `Thiếu ${field}`));
                    errors.push(...outOfRange.map((field) => `${field} phải từ 0 đến 100`));
                    const candidate = { ID: `row-${index}`, NHAN_SU_ID: soHd, MAU_ID: code, HIEU_LUC_TU: from, HIEU_LUC_DEN: to || null, GIA_TRI: values, GHI_CHU: String(row['GHI CHÚ'] ?? '') };
                    if (!errors.length) {
                        // Nhập hàng loạt không tự đóng thỏa thuận cũ; chồng thời gian thì sửa từng người trên màn hình.
                        const plan = planAgreementSuccession([...data.agreements, ...accepted], candidate);
                        if (plan.blocking.length || plan.closable.length) errors.push('Đã có thỏa thuận chồng thời gian');
                    }
                    if (!errors.length) accepted.push(candidate);
                    return { index: index + 2, soHd, name: employeeBySoHd.get(soHd)?.HO_TEN || String(row['HỌ TÊN'] ?? ''), code, values, errors, candidate };
                });
                setPreview(rows);
            } catch (error) {
                toast.error('Không đọc được file Excel: ' + error.message);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const validRows = preview?.filter((row) => !row.errors.length) ?? [];
    const handleImport = async () => {
        try {
            setSaving(true);
            await addAgreements(validRows.map(({ candidate }) => {
                const { ID, ...rest } = candidate;
                return rest;
            }));
            toast.success(`Đã nhập ${validRows.length} thỏa thuận lương`);
            onImported();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title="Nhập thỏa thuận lương từ Excel" subtitle="Tải file mẫu đã điền sẵn nhân sự còn thiếu, điền giá trị rồi chọn file để nhập" icon={FileSpreadsheet} size="max-w-5xl" onClose={onClose} disabled={saving}
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={saving}>Đóng</button>
                    <button className={buttonPrimary} onClick={handleImport} disabled={saving || validRows.length === 0}>
                        <Upload className="w-4 h-4" />{saving ? 'Đang nhập...' : `Nhập ${validRows.length} dòng hợp lệ`}
                    </button>
                </>
            )}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-800">1. Tải file mẫu</p>
                    <select className={inputClass} value={templateCode} onChange={(e) => setTemplateCode(e.target.value)}>
                        {templates.filter((item) => item.HOAT_DONG).map((item) => <option key={item.MA_MAU} value={item.MA_MAU}>{item.TEN_MAU} ({item.MA_MAU})</option>)}
                    </select>
                    <p className="text-xs text-gray-600">Điền sẵn {missingByTemplate.get(templateCode)?.length ?? 0} nhân sự đang dùng mẫu này mà chưa có thỏa thuận. Cột giá trị: {fields.codes.join(', ') || 'không có'}.</p>
                    <button className={buttonSecondary} onClick={handleDownload} disabled={!templateCode}><FileSpreadsheet className="w-4 h-4" />Tải file mẫu</button>
                </div>
                <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-800">2. Chọn file đã điền</p>
                    <input type="file" accept=".xlsx,.xls" onChange={handleFile} className="text-sm" />
                    <p className="text-xs text-gray-600">Số có thể gõ 8.000.000 hoặc 8000000; số lẻ gõ 1,5. Ngày gõ dạng DD/MM/YYYY.</p>
                </div>
            </div>
            {preview && (
                <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[45vh]">
                    <table className={tableClass}>
                        <thead className={`${theadClass} sticky top-0`}>
                            <tr><th className={thClass}>Dòng</th><th className={thClass}>Số HĐ</th><th className={thClass}>Họ tên</th><th className={thClass}>Mẫu</th><th className={thClass}>Giá trị</th><th className={thClass}>Kết quả</th></tr>
                        </thead>
                        <tbody className={tbodyClass}>
                            {preview.map((row) => (
                                <tr key={row.index} className={row.errors.length ? 'bg-red-50' : ''}>
                                    <td className={tdClass}>{row.index}</td><td className={tdClass}>{row.soHd}</td><td className={tdClass}>{row.name}</td><td className={tdClass}>{row.code}</td>
                                    <td className={tdClass}>{Object.entries(row.values).map(([code, value]) => `${code}: ${formatMoney(value)}`).join('; ')}</td>
                                    <td className={tdClass}>{row.errors.length ? <span className="text-red-700">{row.errors.join('; ')}</span> : <Badge tone="green">Hợp lệ</Badge>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
}

const ThoaThuanCongLuong = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [filterTemplate, setFilterTemplate] = useState('ALL');
    const [form, setForm] = useState(null);
    const [detail, setDetail] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [showImport, setShowImport] = useState(false);
    const canView = canViewPayrollPage(PAYROLL_PATHS.agreements);
    const canManage = canManagePayroll();

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            setData(await loadTemplateData());
        } catch (error) {
            console.error('Lỗi tải thỏa thuận lương:', error);
            toast.error('Không tải được thỏa thuận lương');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    const templates = useMemo(() => (data ? assembleTemplates(data.templates, data.templateColumns) : []), [data]);
    const employeeBySoHd = useMemo(() => new Map((data?.employees ?? []).map((employee) => [employee.SO_HD, employee])), [data]);
    const templateByCode = useMemo(() => new Map(templates.map((template) => [template.MA_MAU, template])), [templates]);
    const variableByCode = useMemo(() => new Map((data?.variables ?? []).map((variable) => [canonicalPayrollCode(variable.MA_BIEN), variable])), [data]);

    // Người đang dùng mẫu có khoản thỏa thuận bắt buộc nhưng chưa có thỏa thuận hiệu lực trong tháng này.
    // Mẫu chỉ có khoản tùy chọn (phụ cấp, cơm...) thì thiếu thỏa thuận là bình thường: các khoản đó bằng 0.
    const missingByTemplate = useMemo(() => {
        const result = new Map();
        if (!data) return result;
        const today = new Date();
        const range = getPayrollDateRange(today.getMonth() + 1, today.getFullYear());
        const employees = data.employees.filter((employee) => employee.SO_HD && isEmployeeInPeriod(employee, range));
        const agreementByEmployee = resolveAgreementsAt(data.agreements, range.to);
        const coverage = resolvePeriodEmployees({
            employees,
            teamsByEmployee: buildTeamsByEmployee(employees, data.allocations, range.to),
            agreementByEmployee,
            selectedTemplates: templates.filter((template) => template.HOAT_DONG),
            allTemplates: templates,
        });
        const needsAgreement = new Map(templates.map((template) => [template.MA_MAU, agreementFieldsOf(template, data.variables).required.length > 0]));
        for (const person of coverage.employees) {
            if (!needsAgreement.get(person.templateId) || agreementByEmployee.has(person.employeeId)) continue;
            result.set(person.templateId, [...(result.get(person.templateId) ?? []), person]);
        }
        return result;
    }, [data, templates]);
    const missingPeople = useMemo(() => Array.from(missingByTemplate.values()).flat()
        .filter((person) => matchesSearch(`${person.employeeName} ${person.employeeCode} ${person.team}`, search)), [missingByTemplate, search]);

    const allRows = useMemo(() => (data?.agreements ?? []).map((agreement) => ({ ...agreement, employee: employeeBySoHd.get(agreement.NHAN_SU_ID), status: agreementStatus(agreement) })), [data, employeeBySoHd]);
    const filtered = useMemo(() => allRows.filter((row) => (filterStatus === 'ALL' || filterStatus === 'MISSING' || row.status === filterStatus)
        && (filterTemplate === 'ALL' || row.MAU_ID === filterTemplate)
        && matchesSearch(`${row.NHAN_SU_ID} ${row.employee?.HO_TEN} ${row.employee?.TO}`, search)), [allRows, filterStatus, filterTemplate, search]);
    const accessors = useMemo(() => ({
        employee: (row) => row.employee?.HO_TEN ?? row.NHAN_SU_ID, template: (row) => templateByCode.get(row.MAU_ID)?.TEN_MAU ?? row.MAU_ID,
        effective: (row) => row.HIEU_LUC_TU, status: (row) => row.status,
    }), [templateByCode]);
    const { sorted, sort, toggleSort } = useSortedRows(filtered, accessors, { key: 'employee', direction: 'asc' });
    const { pageRows, pagination } = usePagination(sorted);

    if (!canView) return <AccessDenied />;

    const openForm = (agreement, isNew) => setForm({
        isNew,
        initial: {
            ID: agreement.ID || '',
            NHAN_SU_ID: agreement.NHAN_SU_ID || '',
            MAU_ID: agreement.MAU_ID || '',
            from: toInputDate(agreement.HIEU_LUC_TU || firstDayOfMonth()),
            to: toInputDate(agreement.HIEU_LUC_DEN),
            values: Object.fromEntries(Object.entries(agreement.GIA_TRI ?? {}).map(([code, value]) => [canonicalPayrollCode(code), String(value)])),
            GHI_CHU: agreement.GHI_CHU || '',
        },
    });

    const valueChips = (row) => (
        <div className="flex flex-wrap gap-1">
            {Object.entries(row.GIA_TRI ?? {}).map(([code, value]) => <Badge key={code} tone="purple" title={variableByCode.get(code)?.TEN_BIEN}>{code}: {formatMoney(value)}</Badge>)}
        </div>
    );
    const actions = (row) => (
        <div className="flex justify-center gap-1">
            <IconAction icon={Eye} title="Xem chi tiết" tone="blue" onClick={() => setDetail(row)} />
            {canManage && (
                <>
                    <IconAction icon={Edit} title="Sửa thỏa thuận" onClick={() => openForm(row, false)} />
                    <IconAction icon={TrendingUp} title="Tạo thỏa thuận mới (tăng lương)" tone="green" onClick={() => openForm({ ...row, ID: '', HIEU_LUC_TU: firstDayOfMonth(), HIEU_LUC_DEN: null }, true)} />
                    <IconAction icon={Trash} title="Xóa thỏa thuận" tone="red" onClick={() => setDeleteTarget(row)} />
                </>
            )}
        </div>
    );

    return (
        <PageShell icon={Handshake} title="Thỏa thuận lương" subtitle="Mức lương, đơn giá, phụ cấp, lương đóng bảo hiểm riêng của từng nhân sự theo thời gian"
            actions={(
                <>
                    <button className={buttonSecondary} onClick={reload} disabled={loading}><RefreshCw className="w-4 h-4" />Làm mới</button>
                    {canManage && <button className={buttonSecondary} onClick={() => setShowImport(true)} disabled={!data}><FileSpreadsheet className="w-4 h-4" />Nhập Excel</button>}
                    {canManage && <button className={buttonPrimary} onClick={() => openForm({}, true)} disabled={!data}><Plus className="w-4 h-4" />Thêm thỏa thuận</button>}
                </>
            )}>
            <StatCards active={filterStatus} onSelect={setFilterStatus} cards={[
                { key: 'total', label: 'Tổng thỏa thuận', value: allRows.length, tone: 'indigo', filterValue: 'ALL' },
                { key: 'active', label: 'Đang hiệu lực', value: allRows.filter((row) => row.status === 'ACTIVE').length, tone: 'green', filterValue: 'ACTIVE' },
                { key: 'future', label: 'Sắp hiệu lực', value: allRows.filter((row) => row.status === 'FUTURE').length, tone: 'blue', filterValue: 'FUTURE' },
                { key: 'missing', label: 'Chưa có thỏa thuận', value: Array.from(missingByTemplate.values()).flat().length, tone: 'red', caption: 'Nhân sự dùng mẫu cần thỏa thuận', filterValue: 'MISSING' },
            ]} />
            <ListToolbar search={search} onSearch={setSearch} placeholder="Tìm theo tên, số hợp đồng hoặc tổ..."
                filterCount={[filterStatus, filterTemplate].filter((value) => value !== 'ALL').length}
                onClearFilters={() => { setFilterStatus('ALL'); setFilterTemplate('ALL'); setSearch(''); }}
                filters={(
                    <>
                        <FilterField label="Hiệu lực">
                            <select className={inputClass} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                                <option value="ALL">Tất cả hiệu lực</option>
                                {Object.entries(STATUS_INFO).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
                                <option value="MISSING">Chưa có thỏa thuận</option>
                            </select>
                        </FilterField>
                        <FilterField label="Mẫu lương">
                            <select className={inputClass} value={filterTemplate} onChange={(e) => setFilterTemplate(e.target.value)}>
                                <option value="ALL">Tất cả mẫu lương</option>
                                {templates.map((template) => <option key={template.MA_MAU} value={template.MA_MAU}>{template.TEN_MAU}</option>)}
                            </select>
                        </FilterField>
                    </>
                )} />
            {loading || !data ? <Spinner /> : filterStatus === 'MISSING' ? (
                missingPeople.length === 0 ? <EmptyState icon={Handshake} title="Không còn ai thiếu thỏa thuận">Mọi nhân sự dùng mẫu cần thỏa thuận đều đã có thỏa thuận hiệu lực.</EmptyState> : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className={tableClass}>
                            <thead className={theadClass}>
                                <tr><th className={thClass}>Nhân sự</th><th className={thClass}>Tổ</th><th className={thClass}>Mẫu đang dùng</th><th className={`${thClass} text-center`}>Thao tác</th></tr>
                            </thead>
                            <tbody className={tbodyClass}>
                                {missingPeople.map((person) => (
                                    <tr key={person.employeeId} className="hover:bg-gray-50">
                                        <td className={tdClass}><div className="font-medium text-gray-900">{person.employeeName}</div><div className="text-xs text-gray-500">{person.employeeCode}</div></td>
                                        <td className={tdClass}>{person.team || '—'}</td>
                                        <td className={tdClass}>{person.templateName}</td>
                                        <td className={`${tdClass} text-center`}>
                                            {canManage && <button className={`${buttonSecondary} mx-auto`} onClick={() => openForm({ NHAN_SU_ID: person.employeeId, MAU_ID: person.templateId }, true)}><Plus className="w-4 h-4" />Tạo</button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : sorted.length === 0 ? <EmptyState icon={Handshake} title="Không có thỏa thuận phù hợp">Thêm thỏa thuận hoặc nhập từ Excel.</EmptyState> : (
                <>
                    <div className="space-y-3 md:hidden">
                        {pageRows.map((row) => (
                            <div key={row.ID} className="border border-gray-200 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between items-start gap-2">
                                    <div><p className="font-medium text-gray-900">{row.employee?.HO_TEN || row.NHAN_SU_ID}</p><p className="text-xs text-gray-500">{row.NHAN_SU_ID}{row.employee?.TO ? ` · ${row.employee.TO}` : ''}</p></div>
                                    <Badge tone={STATUS_INFO[row.status].tone}>{STATUS_INFO[row.status].label}</Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div><p className="text-gray-500 text-xs">Mẫu lương</p><p>{templateByCode.get(row.MAU_ID)?.TEN_MAU || row.MAU_ID}</p></div>
                                    <div><p className="text-gray-500 text-xs">Hiệu lực</p><p>{formatDay(row.HIEU_LUC_TU)} - {formatDay(row.HIEU_LUC_DEN) || 'nay'}</p></div>
                                </div>
                                {valueChips(row)}
                                {actions(row)}
                            </div>
                        ))}
                    </div>
                    <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
                        <table className={tableClass}>
                            <thead className={theadClass}>
                                <tr>
                                    <SortableTh label="Nhân sự" sortKey="employee" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Mẫu áp dụng" sortKey="template" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Hiệu lực" sortKey="effective" sort={sort} onSort={toggleSort} />
                                    <th className={thClass}>Thành phần</th>
                                    <SortableTh label="Trạng thái" sortKey="status" sort={sort} onSort={toggleSort} />
                                    <th className={`${thClass} text-center`}>Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className={tbodyClass}>
                                {pageRows.map((row) => (
                                    <tr key={row.ID} className="hover:bg-gray-50 transition-colors">
                                        <td className={tdClass}>
                                            <div className="font-medium text-gray-900">{row.employee?.HO_TEN || <span className="text-red-600">Không còn trong NHANSU</span>}</div>
                                            <div className="text-xs text-gray-500">{row.NHAN_SU_ID}{row.employee?.TO ? ` · ${row.employee.TO}` : ''}</div>
                                        </td>
                                        <td className={tdClass}>{templateByCode.get(row.MAU_ID)?.TEN_MAU || row.MAU_ID}</td>
                                        <td className={`${tdClass} whitespace-nowrap`}>{formatDay(row.HIEU_LUC_TU)} → {formatDay(row.HIEU_LUC_DEN) || 'nay'}</td>
                                        <td className={tdClass}>{valueChips(row)}</td>
                                        <td className={tdClass}><Badge tone={STATUS_INFO[row.status].tone}>{STATUS_INFO[row.status].label}</Badge></td>
                                        <td className={tdClass}>{actions(row)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination pagination={pagination} unit="thỏa thuận" />
                </>
            )}
            {form && <AgreementForm {...form} data={data} templates={templates} onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />}
            {detail && <AgreementDetail row={detail} template={templateByCode.get(detail.MAU_ID)} variableByCode={variableByCode} onClose={() => setDetail(null)} />}
            {showImport && <ImportModal data={data} templates={templates} missingByTemplate={missingByTemplate} onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); reload(); }} />}
            {deleteTarget && (
                <ConfirmDialog title="Xác nhận xóa thỏa thuận lương" confirmLabel="Xóa thỏa thuận" onClose={() => setDeleteTarget(null)}
                    message={<>Xóa thỏa thuận của <b>{deleteTarget.employee?.HO_TEN || deleteTarget.NHAN_SU_ID}</b> (mẫu {templateByCode.get(deleteTarget.MAU_ID)?.TEN_MAU || deleteTarget.MAU_ID}, từ {formatDay(deleteTarget.HIEU_LUC_TU)})?</>}
                    onConfirm={async () => {
                        try {
                            await deleteAgreement(deleteTarget);
                            toast.success('Đã xóa thỏa thuận');
                            setDeleteTarget(null);
                            reload();
                        } catch (error) {
                            toast.error(error.message);
                        }
                    }} />
            )}
        </PageShell>
    );
};

export default ThoaThuanCongLuong;
